import { usePrescriptionAssistant } from '@/components/Patient/components/PrescriptionAssistantTool/usePrescriptionAssistant';
import { PrescriptionPadStep } from '@/components/PatientPageV2/components/PrescriptionPad';
import { useConsultationTracking } from '@/components/PatientPageV2/hooks/useConsultationTracking';
import { usePrescriptionAssistantTracking } from '@/components/PatientPageV2/hooks/usePrescriptionAssistantTracking';
import { getConsultationType } from '@/components/PatientPageV2/utils/getConsultationType';
import { FF_ENABLE_INITIAL_DR_PAYMENT, FF_ENABLE_SHOPIFY_PAYMENT } from '@/constants/featureFlags';
import settings from '@/data/constants';
import { QueryKey } from '@/data/query';
import { getAuthData } from '@/data/service/authService';
import type { UsePostPaymentsChargeForConsultResponse } from '@/hooks/rest/usePostPaymentsChargeForConsult';
import { useAppStore } from '@/state-management';
import { useConsultationStore } from '@/state-management/slices/consultations/hooks/useConsultationStore';
import type { ConsultationModel } from '@/types';
import { Logger } from '@/utils/logger';
import { datadogRum } from '@datadog/browser-rum';
import toast from '@montugroup/design-system/Toast';
import type { PaymentRequestType } from '@montugroup/pms-api-contracts/build/api-pms/consultation/contracts/request-payment.contract';
import { useQueryClient } from '@tanstack/react-query';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import type React from 'react';
import { useState } from 'react';
import { useFeatureFlags } from '../flags';
import { useCanUserChargeConsult } from './useCanUserChargeConsult';
import type { UseChargeConsultProps } from './useChargeConsult';
import useChargeConsult from './useChargeConsult';
import type { ConsultationPaymentStatus, ConsultPaymentStatusData } from './useConsult';
import { useConsult } from './useConsult';
import { useConsultLoggingInfo } from './useConsultLoggingInfo';
import type { SaveConsultationFunc } from './useSaveConsultation';
import { useSendInvoice } from './useSendInvoice';

const logger = new Logger('PMS:useConsultActions');

interface useConsultActionsProps {
  consult?: ConsultationModel;
  setConsult?: React.Dispatch<React.SetStateAction<ConsultationModel>>;
  isConcessionPatient: boolean;
  patientEmail?: string | null;
  patientActive: boolean;
  onCreditCardHasBeenCharged?: (consultId: number) => void;
  onInvoiceHasBeenCreated?: (consultId: number, invoiceCode: string) => void;
  saveConsult: SaveConsultationFunc;
  submitCreatePrescription: () => Promise<Response | null>;
  onPrescriptionActionComplete: (emptyPrescription: boolean) => void | Promise<void>;
  isComplimentary: boolean;
  shouldNotChargeInitialDoctorConsultation?: boolean;
  nextConsultationInWeeks?: number;
}

export const useConsultActions = ({
  consult,
  isConcessionPatient,
  patientEmail,
  patientActive,
  onCreditCardHasBeenCharged,
  onInvoiceHasBeenCreated,
  saveConsult,
  submitCreatePrescription,
  onPrescriptionActionComplete,
  isComplimentary,
  shouldNotChargeInitialDoctorConsultation = false,
  nextConsultationInWeeks,
}: useConsultActionsProps) => {
  const ldClient = useLDClient();
  const { flags } = useFeatureFlags();
  const ffEnableShopifyPayment = flags[FF_ENABLE_SHOPIFY_PAYMENT];
  const ffEnableInitialDrPayment = flags[FF_ENABLE_INITIAL_DR_PAYMENT];

  const [loadingAnyPayment, setLoadingAnyPayment] = useState(false);
  const [loadingConsultCompletion, setLoadingConsultCompletion] = useState(false);
  const [isCompleteConsultationModalOpen, setIsCompleteConsultationModalOpen] = useState(false);
  const { paymentRequestType } = useConsultationStore(consult);

  const getConsultLoggingInfo = useConsultLoggingInfo({ consult, isConcessionPatient });
  const queryClient = useQueryClient();
  const { trackReviewScript } = usePrescriptionAssistantTracking();
  const { trackCompleteConsultation } = useConsultationTracking();

  const { isDoc } = getAuthData();

  const {
    prescriptionPad: { step: createScriptStep, showCancellation },
    prescriptionHistory: { itemsToAction, itemsToCancel },
  } = useAppStore.use.prescriptionAssistant();
  const hasUserInteractedWithPrescriptions = useAppStore.use.isPrescriptionInProgress()();
  const updateShowCancellation = useAppStore.use.updateShowCancellation();
  const updatePrescriptionPadStep = useAppStore.use.updatePrescriptionPadStep();

  const hasItemsToCancel = itemsToCancel.length > 0;
  const hasItemsToAction = itemsToAction.length === 0;

  const { paymentMethod, paymentState } = useConsult({
    paymentStatus: consult?.payment_status as ConsultationPaymentStatus,
    paymentDetails: consult?.paymentStatus as ConsultPaymentStatusData,
    paidDate: consult?.paid_date ?? '',
    paymentQueued: consult?.payment_queued,
    invoiceCode: consult?.xero_consultation_code,
  });

  const isConsultationValid = (
    showConsultation: ConsultationModel | undefined,
    patientActive: boolean,
  ): showConsultation is ConsultationModel => {
    return !!(showConsultation && showConsultation.id && patientActive);
  };

  const { canUserChargeConsult } = useCanUserChargeConsult({
    consultationType: consult?.consult_type,
    doctorId: consult?.doctor_id ?? undefined,
  });

  const { sendInvoice } = useSendInvoice();

  const {
    createShopifyPaymentRequest,
    doChargeConcession,
    doChargeConsult,
    consultDiscountRate,
    handleChangeConsultDiscountRate,
  } = useChargeConsult({
    consultationId: consult?.id,
    consultationType: consult?.consult_type as UseChargeConsultProps['consultationType'],
    doctorId: consult?.doctor_id ?? undefined,
    patientEmail: patientEmail,
    consultationPaymentMethod: paymentMethod,
    isConcessionPatient,
    isComplimentary,
  });

  const { resetPrescriptionAssistant } = usePrescriptionAssistant();

  const consultAndClinicianType = getConsultationType({
    consult_type: consult?.consult_type,
    doctor_id: consult?.doctor_id ?? undefined,
  });

  const processChargeCreditCardResult = (consultId: number, error: boolean) => {
    logger.info('processChargeCreditCardResult', getConsultLoggingInfo());
    const isSuccess = !error;
    if (isSuccess) {
      toast.success('Credit card charge request added to queue');
      onCreditCardHasBeenCharged?.(consultId);
    } else {
      toast.error('Something went wrong charging credit card');
      logger.error('processChargeCreditCardResult', getConsultLoggingInfo());
    }
  };

  const processChargeInvoiceResult = (
    consultId: number,
    data: UsePostPaymentsChargeForConsultResponse | null,
    error: boolean,
  ) => {
    const isSuccess = !error;
    const invoiceCode = data?.xero_consultation_code ?? '';
    logger.info('processChargeInvoiceResult', getConsultLoggingInfo({ isSuccess, invoiceCode }));

    if (isSuccess) {
      toast.success('User has been invoiced');
      if (invoiceCode) {
        onInvoiceHasBeenCreated?.(consultId, invoiceCode);
      }
    } else {
      logger.error(
        'processChargeInvoiceResult:: Could not generate invoice',
        getConsultLoggingInfo({ isSuccess, invoiceCode }),
      );
      toast.error('Could not generate invoice');
    }
  };

  const processChargeResult = (
    consultId: number,
    data: UsePostPaymentsChargeForConsultResponse | null,
    error: boolean,
  ) => {
    logger.info('processChargeResult', getConsultLoggingInfo({ paymentMethod, paymentState }));
    ldClient?.track('consultation-charge', {
      consultAndClinicianType,
    });
    datadogRum.addAction('consultation-charge', {
      consultAndClinicianType,
    });
    if (paymentMethod === 'CREDIT_CARD') {
      processChargeCreditCardResult(consultId, error);
      return;
    }

    processChargeInvoiceResult(consultId, data, error);
  };

  const triggerInvoice = async (consultationCode: string) => {
    let wasSuccess;

    try {
      await sendInvoice({ consultationCode });
      wasSuccess = true;
      toast.success('Invoice Email to Patient sent successfully');
    } catch (_err) {
      wasSuccess = false;
      toast.error('Email failed to send');
    }

    logger.info(
      'triggerInvoice',
      getConsultLoggingInfo({
        wasSuccess,
      }),
    );

    return wasSuccess;
  };

  const handleConsultPayment = async () => {
    logger.info(
      'paymentsButtonAction',
      getConsultLoggingInfo({
        paymentState,
      }),
    );

    if (!consult) {
      return;
    }

    setLoadingAnyPayment(true);

    if (ffEnableInitialDrPayment) {
      if (shouldNotChargeInitialDoctorConsultation) {
        setLoadingAnyPayment(false);
        return;
      }
      if (paymentState === 'NOT_PAID') {
        await createShopifyPaymentRequest(consult.pricingTier!, paymentRequestType ?? undefined);
      } else if (!consult?.pricingTier && consult?.xero_consultation_code) {
        //triggers resending an invoice if they had been sent an invoice previously and not entered the new shopify checkout flow
        await triggerInvoice(consult.consultation_code);
      }
    } else if (ffEnableShopifyPayment) {
      if (paymentState === 'NOT_PAID') {
        await createShopifyPaymentRequest(consult.pricingTier!, undefined);
      } else if (!consult?.pricingTier && consult?.xero_consultation_code) {
        //triggers resending an invoice if they had been sent an invoice previously and not entered the new shopify checkout flow
        await triggerInvoice(consult.consultation_code);
      }
    } else {
      if (paymentState === 'NOT_PAID') {
        let result = null;
        if (isConcessionPatient) {
          result = await doChargeConcession();
        } else {
          result = await doChargeConsult();
        }
        processChargeResult(consult.id, result.data, result.error !== null);
      } else {
        // Assumes that this function will either resend an invoice or resend a receipt for credit cards
        await triggerInvoice(consult.consultation_code);
      }
    }

    setLoadingAnyPayment(false);
  };

  const handleConcessionPayment = async () => {
    if (!consult) {
      return;
    }

    setLoadingAnyPayment(true);

    const result = await doChargeConcession();
    processChargeResult(consult.id, result.data, result.error !== null);

    setLoadingAnyPayment(false);
  };

  const handleShopifyPaymentRequest = async (paymentRequestType?: PaymentRequestType) => {
    if (consult?.pricingTier !== undefined) {
      setLoadingAnyPayment(true);

      if (paymentRequestType) {
        await createShopifyPaymentRequest(consult.pricingTier, paymentRequestType);
      } else {
        await createShopifyPaymentRequest(consult.pricingTier);
      }

      setLoadingAnyPayment(false);
    }
  };

  const handleClearPrescription = () => {
    resetPrescriptionAssistant();
  };

  const handleNoShow = async () => {
    if (!consult) {
      return;
    }

    trackCompleteConsultation(consult.id);

    await saveConsult(
      {
        id: consult.id,
        notes: consult.notes,
        active: false,
        status_id: settings.consultationStatus.noShow,
      },
      {
        askConfirmation: false,
      },
    );
  };

  const runConsultationPrescriptionFlow = async () => {
    if (!consult) {
      return;
    }

    trackCompleteConsultation(consult.id);

    try {
      setLoadingConsultCompletion(true);
      await saveConsult(
        {
          id: consult.id,
          notes: consult.notes,
          next_consultation_in_weeks: nextConsultationInWeeks,
          status_id: settings.consultationStatus.completed,
        },
        {
          askConfirmation: false,
          refetchData: true,
        },
      );
    } catch (error) {
      logger.error('Failed to save consultation', {
        error,
        consultationId: consult.id,
      });
      throw new Error('Failed to save consultation');
    } finally {
      setLoadingConsultCompletion(false);
    }

    // For follow up doctor consultation we automatically charge on consultation completion
    if (isDoc && canUserChargeConsult) {
      try {
        await handleConsultPayment();
      } catch (error) {
        // At this stage we don't want to prevent script creation or show toast on payment error so we just log error
        logger.error('Error collecting automatic payment for follow-up doc consultation', error);
      }
    }
  };

  const makeSaveScriptAndConsultationRequest = async () => {
    logger.info('makeSaveScriptAndConsultationRequest', getConsultLoggingInfo());
    try {
      const response = await submitCreatePrescription();

      if (response) {
        resetPrescriptionAssistant();

        logger.info('makeSaveScriptAndConsultationRequest:: Prescription Created', getConsultLoggingInfo());
        toast.success('Prescription Created');
      }

      await runConsultationPrescriptionFlow();
      await onPrescriptionActionComplete(response === null);

      queryClient.invalidateQueries({ queryKey: [QueryKey.GetPatientConsultations] });
    } catch {
      logger.error(
        'makeSaveScriptAndConsultationRequest:: Failed to Create! Please try again later.',
        getConsultLoggingInfo(),
      );
      toast.error('Failed to Create! Please try again later.');
    }
  };

  const moveToNextConsultStep = async () => {
    if (!isConsultationValid(consult, patientActive)) {
      return;
    }

    if (hasItemsToAction && hasItemsToCancel && !showCancellation) {
      updateShowCancellation(true);
      return;
    }

    if (!hasUserInteractedWithPrescriptions || createScriptStep === PrescriptionPadStep.Confirm) {
      setLoadingConsultCompletion(true);
      await makeSaveScriptAndConsultationRequest();
      setLoadingConsultCompletion(false);
      return;
    }

    if (hasUserInteractedWithPrescriptions) {
      if (createScriptStep === PrescriptionPadStep.Create) {
        trackReviewScript(consult.id);
        updatePrescriptionPadStep(PrescriptionPadStep.Confirm);
      } else {
        updatePrescriptionPadStep(PrescriptionPadStep.Create);
      }
    }
  };

  const handleCompleteConsultation = () => {
    if (!hasUserInteractedWithPrescriptions && createScriptStep === PrescriptionPadStep.Create) {
      setIsCompleteConsultationModalOpen(true);
      return;
    }

    moveToNextConsultStep();
  };

  const handleCloseCompleteConsultationModal = () => {
    setIsCompleteConsultationModalOpen(false);
  };

  const handleConfirmCompleteConsult = () => {
    handleCloseCompleteConsultationModal();
    moveToNextConsultStep();
  };

  const onOnlyCancelComplete = async () => {
    await runConsultationPrescriptionFlow();
    onPrescriptionActionComplete(true);
  };

  return {
    loadingAnyPayment,
    loadingConsultCompletion,
    consultDiscountRate,
    isCompleteConsultationModalOpen,
    handleChangeConsultDiscountRate,
    handleConsultPayment,
    handleConcessionPayment,
    handleShopifyPaymentRequest,
    handleCompleteConsultation,
    handleClearPrescription,
    handleNoShow,
    handleCloseCompleteConsultationModal,
    handleConfirmCompleteConsult,
    onOnlyCancelComplete,
  };
};
